import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Flask,
  Heartbeat,
  ChartLineUp,
  Timer,
  UsersThree,
  SignOut,
  Buildings,
} from "@phosphor-icons/react";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Mood Scan", icon: Heartbeat, testId: "nav-mood-scan" },
  { to: "/formulas", label: "Formulas", icon: Flask, testId: "nav-formulas" },
  { to: "/sessions", label: "Sessions", icon: Timer, testId: "nav-sessions" },
  { to: "/tracking", label: "Tracking", icon: ChartLineUp, testId: "nav-tracking" },
  { to: "/community", label: "Community", icon: UsersThree, testId: "nav-community" },
];

export default function Navigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "org_admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      {/* Desktop Top Nav */}
      <nav className="glass-nav fixed top-0 left-0 right-0 z-50 hidden md:flex items-center justify-between px-8 h-16" data-testid="desktop-nav">
        <NavLink to="/dashboard" className="flex items-center gap-2">
          <img src="/logo.png" alt="Mood Chemistry Lab" className="h-9 w-auto" />
        </NavLink>

        <div className="flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  isActive
                    ? "bg-[#2D6A4F] text-white shadow-md"
                    : "text-[#78857F] hover:text-[#2D6A4F] hover:bg-[#2D6A4F]/5"
                }`
              }
            >
              <item.icon size={18} weight="duotone" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  isActive
                    ? "bg-[#F4A261] text-white shadow-md"
                    : "text-[#F4A261] hover:bg-[#F4A261]/10"
                }`
              }
            >
              <Buildings size={16} weight="duotone" />
              Admin
            </NavLink>
          )}
          {user && (
            <span className="text-xs text-[#78857F]">
              {user.name}
            </span>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="p-2 rounded-full text-[#78857F] hover:text-[#E76F51] hover:bg-[#E76F51]/10 transition-all duration-300"
          >
            <SignOut size={20} weight="duotone" />
          </button>
        </div>
      </nav>

      {/* Mobile Bottom Nav */}
      <nav className="glass-nav fixed bottom-0 left-0 right-0 z-50 flex md:hidden items-center justify-around px-2 h-16 safe-area-bottom" data-testid="mobile-nav">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`mobile-${item.testId}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all duration-300 ${
                isActive
                  ? "text-[#2D6A4F]"
                  : "text-[#78857F]"
              }`
            }
          >
            <item.icon size={22} weight="duotone" />
            <span className="text-[10px] font-medium">{item.label}</span>
          </NavLink>
        ))}
        <button
          onClick={handleLogout}
          data-testid="mobile-logout-button"
          className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl text-[#78857F] transition-all"
        >
          <SignOut size={22} weight="duotone" />
          <span className="text-[10px] font-medium">Logout</span>
        </button>
      </nav>
    </>
  );
}
